import { AxiosResponse } from 'axios';

import { initializeApiInterceptors } from './axiosInterceptors';
import { HttpClient } from './httpClient';

export type ResponseHandler<T = any, R = T> = (response: AxiosResponse<T>) => R;

interface ApiServiceGetConfig {
	params: any;
	headers?: any;
}

/**
 * Validates that the response status is in the 2xx–3xx range.
 * Throws the response body if the status indicates failure.
 * @param response - The Axios response to validate.
 * @returns The same response if successful.
 * @throws The response data when the status is 400 or above.
 */
function statusChecker<T>(response: AxiosResponse<T>) {
	// Accept 2xx (success) and 3xx (redirect) status codes as successful
	if (response?.status >= 200 && response?.status < 400) {
		return response;
	}

	throw response?.data;
}

/**
 * Primary API service for all standard application requests.
 * Wraps the shared primary {@link HttpClient} instance and provides
 * typed convenience methods for GET, POST, PUT, PATCH, DELETE and file upload.
 *
 * Attach request/response interceptors via {@link initializeApiInterceptors},
 * which is called automatically in the constructor.
 *
 * @example
 * const data = await apiService.get('/users', { params: { page: 1 } });
 */
class ApiService {
	private _httpClient = HttpClient.getInstance();
	private _customBaseUrl: string | null = null;

	constructor() {
		initializeApiInterceptors(this._httpClient);
	}

	/** Default response handler — extracts `data` from the Axios response. Returns `null` when data is absent. */
	responseHandler: ResponseHandler = ({ data }) => data ?? null;

	/**
	 * Override the base URL at runtime.
	 * @param baseUrl - The new base URL to use for subsequent requests.
	 */
	setBaseUrl(baseUrl: string) {
		this._customBaseUrl = baseUrl;
		// Note: HttpClient.getInstance() is used, so we can't recreate it here
		// The base URL change would need to be handled at the HttpClient level if needed
	}

	/**
	 * Returns the currently active base URL.
	 * Falls back to `REACT_APP_API_URL` when no custom URL has been set.
	 */
	getBaseUrl(): string {
		return this._customBaseUrl || process.env.REACT_APP_API_URL || '';
	}

	/**
	 * Performs an HTTP GET request.
	 * @param url - Endpoint path relative to the base URL.
	 * @param config - Optional query params and custom headers.
	 */
	async get<T = any>(url: string, config?: ApiServiceGetConfig): Promise<AxiosResponse<T>> {
		const { params, headers } = config ?? {};

		return statusChecker(await this._httpClient.get<T>(url, { params, headers }));
	}

	/**
	 * Performs an HTTP POST request.
	 * @param url - Endpoint path relative to the base URL.
	 * @param body - Optional request body.
	 * @param headers - Optional additional headers.
	 */
	async post<T = any, B = any>(url: string, body?: B, headers?: any): Promise<AxiosResponse<T>> {
		return statusChecker(await this._httpClient.post<T>(url, body, { headers }));
	}

	/**
	 * Performs a multipart/form-data POST request for file uploads.
	 * @param url - Endpoint path relative to the base URL.
	 * @param body - FormData or multipart body.
	 */
	async postUpload<T = any, B = any>(url: string, body?: B): Promise<AxiosResponse<T>> {
		return statusChecker(
			await this._httpClient.post<T>(url, body, {
				headers: { 'Content-Type': 'multipart/form-data' },
			})
		);
	}

	/**
	 * Performs an HTTP PUT request (full resource replacement).
	 * @param url - Endpoint path relative to the base URL.
	 * @param body - Full resource body.
	 */
	async put<T = any, B = any>(url: string, body: B): Promise<AxiosResponse<T>> {
		return statusChecker(await this._httpClient.put<T>(url, body));
	}

	/**
	 * Performs an HTTP DELETE request.
	 * @param url - Endpoint path relative to the base URL.
	 */
	async delete(url: string): Promise<AxiosResponse<null>> {
		return statusChecker(await this._httpClient.delete(url));
	}

	/**
	 * Performs an HTTP PATCH request using `application/merge-patch+json`.
	 * @param url - Endpoint path relative to the base URL.
	 * @param body - Partial resource body containing only the fields to update.
	 */
	async patch<T = any, B = any>(url: string, body: B): Promise<AxiosResponse<T>> {
		return statusChecker(
			await this._httpClient.patch<T>(url, body, {
				headers: { 'Content-Type': 'application/merge-patch+json' },
			})
		);
	}
}

export const apiService = new ApiService();
